// AIE-539: shared preset palette for study labels (tags).
//
// Keys MUST stay in sync with the backend palette (backend/models/study_labels.py)
// and the DB CHECK constraint (20260701000000_add_study_labels.sql). No free hex
// picker in v1. The first key is the default color for a new label.

export const LABEL_COLOR_KEYS = [
  "blue",
  "teal",
  "green",
  "amber",
  "orange",
  "red",
  "violet",
  "slate",
] as const;

export type LabelColorKey = (typeof LABEL_COLOR_KEYS)[number];

export const DEFAULT_LABEL_COLOR: LabelColorKey = LABEL_COLOR_KEYS[0];

export const MAX_LABEL_LENGTH = 50;

interface LabelColorStyle {
  /** Classes for the always-visible chip (bg + text + border). */
  chip: string;
  /** Solid background for a swatch button in the color picker. */
  swatch: string;
}

// Full literal class strings so Tailwind's content scanner keeps them.
export const LABEL_COLOR_STYLES: Record<LabelColorKey, LabelColorStyle> = {
  blue: {
    chip: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    swatch: "bg-blue-500",
  },
  teal: {
    chip: "bg-teal-500/15 text-teal-300 border-teal-500/30",
    swatch: "bg-teal-500",
  },
  green: {
    chip: "bg-green-500/15 text-green-300 border-green-500/30",
    swatch: "bg-green-500",
  },
  amber: {
    chip: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    swatch: "bg-amber-500",
  },
  orange: {
    chip: "bg-orange-500/15 text-orange-300 border-orange-500/30",
    swatch: "bg-orange-500",
  },
  red: {
    chip: "bg-red-500/15 text-red-300 border-red-500/30",
    swatch: "bg-red-500",
  },
  violet: {
    chip: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    swatch: "bg-violet-500",
  },
  slate: {
    chip: "bg-slate-500/20 text-slate-300 border-slate-500/40",
    swatch: "bg-slate-500",
  },
};

export function isLabelColorKey(value: unknown): value is LabelColorKey {
  return (
    typeof value === "string" &&
    (LABEL_COLOR_KEYS as readonly string[]).includes(value)
  );
}

/** Resolve chip/swatch styles for a stored color key, falling back to the default. */
export function getLabelColorStyle(color?: string | null): LabelColorStyle {
  if (isLabelColorKey(color)) {
    return LABEL_COLOR_STYLES[color];
  }
  return LABEL_COLOR_STYLES[DEFAULT_LABEL_COLOR];
}
